import { cn } from '@/lib/cn';
import { type JSX, Show, createUniqueId, splitProps } from 'solid-js';

export interface TextareaProps extends JSX.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = (props: TextareaProps): JSX.Element => {
  const [local, rest] = splitProps(props, ['label', 'hint', 'error', 'class', 'id']);
  const id = local.id ?? `textarea-${createUniqueId()}`;
  return (
    <div class="flex flex-col gap-1 text-sm">
      <Show when={local.label}>
        <label
          for={id}
          class={cn(
            'font-medium text-neutral-700 dark:text-neutral-300',
            rest.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {local.label}
        </label>
      </Show>
      <textarea
        id={id}
        class={cn(
          'min-h-24 rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 outline-none transition motion-reduce:transition-none',
          'border-neutral-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-500',
          'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:focus:border-brand-500',
          local.error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
          local.class as string | undefined,
        )}
        {...rest}
      />
      <Show when={local.error}>
        <span class="text-xs text-red-600">{local.error}</span>
      </Show>
      <Show when={!local.error && local.hint}>
        <span class="text-xs text-neutral-500">{local.hint}</span>
      </Show>
    </div>
  );
};
